import { TFunction } from '@lyearn/i18n';
import { FieldType } from '@/modules/corporate/modules/database/interfaces';
import { WELCOME_BLOCK_ID } from '@/modules/corporate/modules/survey/views/SurveyEditor/types';
import { adaptTagsOnEdit, adaptTagsOnSubmit } from '@/modules/corporate/modules/tags/utils';
import { DatabaseFieldTypeEnum, EntityTagType, UpdateSurveyInput } from '@/types/schema';

import { SurveyEditorDetailsFragment } from '../../graphql/fragments/generated/SurveyEditorDetails';
import { SurveyDatabaseFieldsState, SurveyEditorUIState } from './types';

export const adaptToUpdateSurveyInput = (survey: SurveyEditorDetailsFragment) => {
  const { id, name, description, status, cover, tags } = survey;

  return {
    id,
    name,
    description,
    status,
    cover,
    tags: adaptTagsOnSubmit(adaptTagsOnEdit(tags as EntityTagType[] | null)),
  };
};

interface AdaptFieldsStateParams {
  viewId: string;
  dbMetadataId: string;
  fieldsState: SurveyDatabaseFieldsState;
}

export const adaptFieldsState = (params: AdaptFieldsStateParams): UpdateSurveyInput['dbFields'] => {
  const { dbMetadataId, fieldsState, viewId } = params;

  return {
    upsertDatabaseMetadataFields: { dbMetadataId, fields: fieldsState.metadataFields },
    upsertDatabaseViewFields: { viewId, fields: fieldsState.viewFields },
  };
};

export const validateUpdateSurveyInput = (
  t: TFunction,
  surveyName: string,
  fields: FieldType[],
) => {
  const errors: SurveyEditorUIState['errors'] = {};

  const visibleFields = fields.filter(({ viewField }) => viewField.visible);

  if (!visibleFields?.length) {
    errors[WELCOME_BLOCK_ID] = {
      type: 'AtleastOneBlock',
      message: t('SurveyEditor:Errors.AtleastOneBlock'),
    };
  } else if (!surveyName.trim().length) {
    errors[WELCOME_BLOCK_ID] = {
      type: 'TitleCantBeEmpty',
      message: t('SurveyEditor:Errors.TitleCantBeEmpty'),
    };
  }

  visibleFields?.forEach(({ viewField, metadataField }) => {
    const { label, fieldId } = viewField;
    if (!label?.trim().length) {
      errors[fieldId] = {
        type: 'QuestionCantBeEmpty',
        message: t('SurveyEditor:Errors.QuestionCantBeEmpty'),
      };
    } else {
      const isSelectField =
        metadataField.type === DatabaseFieldTypeEnum.Select ||
        metadataField.type === DatabaseFieldTypeEnum.MultiSelect;

      if (!isSelectField) {
        return;
      }

      const hasEmptyOptionText = metadataField?.options?.find(({ value }) => !value?.trim().length);
      // TODO: move to config
      if (!metadataField?.options?.length || hasEmptyOptionText) {
        errors[fieldId] = {
          type: 'OptionCannotBeEmpty',
          message: t('SurveyEditor:Errors.OptionCannotBeEmpty'),
        };
      }
    }
  });

  return errors;
};

interface GetErrorsToShowParams {
  prevErrors: SurveyEditorUIState['errors'];
  nextErrors: SurveyEditorUIState['errors'];
}

export const getErrorsToShow = ({ prevErrors, nextErrors }: GetErrorsToShowParams) => {
  const errors: SurveyEditorUIState['errors'] = {};

  Object.entries(prevErrors).forEach(([id, error]) => {
    const nextError = nextErrors[id];

    if (nextError?.type === error.type) {
      errors[id] = error;
    }
  });

  return errors;
};
